/**
 * MUI SpeedDial hard-codes role="menu" on the actions container and role="menuitem"
 * on each SpeedDialAction Fab. When closed (or with static tooltips), axe / agent
 * audits fail aria-required-children. When we only rewrite the parent to group,
 * open actions still fail aria-required-parent (menuitem must live under menu/menubar).
 *
 * A speed dial is a labeled group of buttons, not a menu. Patch parent → group and
 * strip menuitem from action controls so native button semantics remain valid.
 */

import { useLayoutEffect, type RefObject } from 'react';

function speedDialActionsIdFromAriaLabel(ariaLabel: string): string {
  return `${String(ariaLabel || '').replace(/^[^a-z]+|[^\w:.-]+/gi, '')}-actions`;
}

export function patchMuiSpeedDialActionsRole(
  ariaLabel: string,
  root?: HTMLElement | null,
): void {
  const id = speedDialActionsIdFromAriaLabel(ariaLabel);
  const fromRoot = root?.querySelector(`#${id}`) as HTMLElement | null;
  const actions =
    fromRoot ||
    (typeof document !== 'undefined' ? document.getElementById(id) : null);
  if (!actions) return;

  if (actions.getAttribute('role') === 'menu') {
    actions.setAttribute('role', 'group');
    actions.removeAttribute('aria-orientation');
  }
  if (actions.getAttribute('role') === 'group' && !actions.getAttribute('aria-label')) {
    actions.setAttribute('aria-label', ariaLabel);
  }

  // menuitem requires menu/menubar ancestors; group alone is not enough for audits.
  const menuItems = actions.querySelectorAll('[role="menuitem"]');
  menuItems.forEach((node) => {
    node.removeAttribute('role');
  });
}

export function useMuiSpeedDialAccessibleActionsRole(
  ariaLabel: string,
  open: boolean,
  rootRef?: RefObject<HTMLElement | null>,
): void {
  useLayoutEffect(() => {
    patchMuiSpeedDialActionsRole(ariaLabel, rootRef?.current ?? null);
    const id = speedDialActionsIdFromAriaLabel(ariaLabel);
    const actions = document.getElementById(id);
    if (!actions || typeof MutationObserver === 'undefined') {
      return undefined;
    }
    const observer = new MutationObserver(() => {
      patchMuiSpeedDialActionsRole(ariaLabel, rootRef?.current ?? null);
    });
    observer.observe(actions, {
      attributes: true,
      attributeFilter: ['role', 'class'],
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, [ariaLabel, open, rootRef]);
}
